import {
  ChangeDetectionStrategy,
  Component,
  output,
  inject,
} from '@angular/core';

import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  CheckboxWithLabelComponent,
  EmailValidator,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengPasswordDirective,
  ReCaptchaComponent,
} from '@web/shared/ui';
import { PasswordModule } from 'primeng/password';
import { InputTextModule } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { generateRandomPassword } from '@web/utils';
import { SignUpAsClientForm } from '../../models/sign-up-as-client-form';
import { CheckboxModule } from 'primeng/checkbox';
import { PrivacyPolicyClauseComponent } from '../../ui/privacy-policy-clause/privacy-policy-clause.component';
import { NewsletterCheckboxLabelComponent } from '../../ui/newsletter-checkbox-label/newsletter-checkbox-label.component';
import { RegulationsCheckboxLabelComponent } from '../../ui/regulations-checkbox-label/regulations-checkbox-label.component';

type SignUpAsClientFormGroup = FormGroup<{
  email: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  newsletter: FormControl<boolean>;
  password: FormControl<string>;
  recaptcha: FormControl<boolean>;
  regulations: FormControl<boolean>;
  username: FormControl<string>;
}>;

@Component({
  selector: 'app-sign-up-as-client-form',
  imports: [
    Button,
    CheckboxModule,
    FormControlComponent,
    InputTextModule,
    NewsletterCheckboxLabelComponent,
    PasswordModule,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    PrimengPasswordDirective,
    PrivacyPolicyClauseComponent,
    ReactiveFormsModule,
    RegulationsCheckboxLabelComponent,
    CheckboxWithLabelComponent,
    ReCaptchaComponent,
  ],
  templateUrl: './sign-up-as-client-form.component.html',
  styleUrl: './sign-up-as-client-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignUpAsClientFormComponent {
  private readonly builder = inject(FormBuilder);

  public readonly submitClickEvent = output<SignUpAsClientForm>();

  protected readonly form = this.buildForm();

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  public handleGeneratePasswordClick(): void {
    this.form.controls.password.patchValue(generateRandomPassword());
  }

  private toData(): SignUpAsClientForm {
    const {
      username,
      email,
      password,
      firstName,
      lastName,
      regulations,
      newsletter,
    } = this.form.getRawValue();

    return new SignUpAsClientForm(
      username,
      email,
      password,
      firstName,
      lastName,
      regulations,
      newsletter,
    );
  }

  private buildForm(): SignUpAsClientFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      recaptcha: this.builder.nonNullable.control<boolean>(false, [Validators.required, Validators.requiredTrue]),
      username: this.builder.nonNullable.control('', [Validators.required]),
      email: this.builder.nonNullable.control('', [EmailValidator.email, Validators.required]),
      password: this.builder.nonNullable.control('', [Validators.required]),
      firstName: this.builder.nonNullable.control('', [Validators.required]),
      lastName: this.builder.nonNullable.control('', [Validators.required]),
      regulations: this.builder.nonNullable.control(false, [Validators.required, Validators.requiredTrue]),
      newsletter: this.builder.nonNullable.control(false),
    });
  }
}
